/**
 * PATH: apps/qa/src/pages/new-inspection/useFloorManager.ts
 * PURPOSE: Floor/area state management and manipulation helpers for wizard
 */

import { useState, useEffect, useCallback } from 'react'
import { FLOOR_TEMPLATES, QUICK_TEMPLATES } from '@/lib/inspection-constants'
import type { FloorDraft, AreaDraft, LocalAttachment } from './inspection-types'

const generateId = () => Math.random().toString(36).substr(2, 9)

function createAreaDraft(name: string, code: string): AreaDraft {
  return { id: generateId(), area_name: name, area_code: code, notes: '', trade: '', attachments: [], isExpanded: false }
}

function generateAreaCode(floorName: string, areaName: string, existing: AreaDraft[]) {
  const fa = floorName.split(' ').map(w => w[0]?.toUpperCase() || '').join('')
  const aa = areaName.split(' ').map(w => w[0]?.toUpperCase() || '').join('')
  let code = `${fa}-${aa}`, suffix = 1
  while (existing.some(a => a.area_code === code)) { code = `${fa}-${aa}${suffix}`; suffix++ }
  return code
}

export function useFloorManager() {
  const [floors, setFloors] = useState<FloorDraft[]>([])

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => { floors.forEach(f => f.areas.forEach(a => a.attachments.forEach(att => { if (att.preview) URL.revokeObjectURL(att.preview) }))) }
  }, [])

  const addFloor = useCallback((template?: typeof FLOOR_TEMPLATES[0]) => {
    const name = template?.name || `Floor ${floors.length + 1}`
    setFloors(prev => [...prev, {
      id: generateId(), floor_name: name, notes: '',
      areas: template?.areas.map(a => createAreaDraft(a, generateAreaCode(name, a, []))) || [],
    }])
  }, [floors.length])

  const removeFloor = useCallback((id: string) => setFloors(prev => prev.filter(f => f.id !== id)), [])
  const updateFloor = useCallback((id: string, name: string) => setFloors(prev => prev.map(f => f.id === id ? { ...f, floor_name: name } : f)), [])
  const updateFloorNotes = useCallback((id: string, notes: string) => setFloors(prev => prev.map(f => f.id === id ? { ...f, notes } : f)), [])

  const addArea = useCallback((floorId: string, areaName?: string) => {
    setFloors(prev => prev.map(f => {
      if (f.id !== floorId) return f
      const name = areaName || `Area ${f.areas.length + 1}`
      return { ...f, areas: [...f.areas, createAreaDraft(name, generateAreaCode(f.floor_name, name, f.areas))] }
    }))
  }, [])

  const addBulkAreas = useCallback((floorId: string, text: string) => {
    const names = text.split(/[\n,;]+/).map(s => s.trim()).filter(s => s.length > 0)
    if (names.length === 0) return
    setFloors(prev => prev.map(f => {
      if (f.id !== floorId) return f
      const newAreas: AreaDraft[] = []
      for (const name of names) {
        const existing = [...f.areas, ...newAreas]
        newAreas.push(createAreaDraft(name, generateAreaCode(f.floor_name, name, existing)))
      }
      return { ...f, areas: [...f.areas, ...newAreas] }
    }))
  }, [])

  const removeArea = useCallback((floorId: string, areaId: string) => {
    setFloors(prev => prev.map(f => f.id !== floorId ? f : { ...f, areas: f.areas.filter(a => a.id !== areaId) }))
  }, [])

  const updateArea = useCallback((floorId: string, areaId: string, updates: Partial<AreaDraft>) => {
    setFloors(prev => prev.map(f => f.id !== floorId ? f : { ...f, areas: f.areas.map(a => a.id === areaId ? { ...a, ...updates } : a) }))
  }, [])

  const toggleAreaExpanded = useCallback((floorId: string, areaId: string) => {
    setFloors(prev => prev.map(f => f.id !== floorId ? f : { ...f, areas: f.areas.map(a => a.id === areaId ? { ...a, isExpanded: !a.isExpanded } : a) }))
  }, [])

  const addAreaAttachments = useCallback((floorId: string, areaId: string, files: FileList | null) => {
    if (!files || files.length === 0) return
    const newAtts: LocalAttachment[] = Array.from(files).map(file => {
      const isImage = file.type.startsWith('image/')
      return { id: generateId(), file, type: isImage ? 'photo' as const : 'document' as const, preview: isImage ? URL.createObjectURL(file) : null, name: file.name }
    })
    setFloors(prev => prev.map(f => f.id !== floorId ? f : { ...f, areas: f.areas.map(a => a.id === areaId ? { ...a, attachments: [...a.attachments, ...newAtts] } : a) }))
  }, [])

  const removeAreaAttachment = useCallback((floorId: string, areaId: string, attId: string) => {
    setFloors(prev => prev.map(f => {
      if (f.id !== floorId) return f
      return { ...f, areas: f.areas.map(a => {
        if (a.id !== areaId) return a
        const removed = a.attachments.find(att => att.id === attId)
        if (removed?.preview) URL.revokeObjectURL(removed.preview)
        return { ...a, attachments: a.attachments.filter(att => att.id !== attId) }
      })}
    }))
  }, [])

  const applyTemplate = useCallback((template: typeof QUICK_TEMPLATES[0]) => {
    setFloors(template.floors.map(f => ({
      id: generateId(), floor_name: f.name, notes: '',
      areas: f.areas.map(a => createAreaDraft(a, generateAreaCode(f.name, a, []))),
    })))
  }, [])

  const applyGeneratedFloors = useCallback((generatedFloors: { id: string; name: string; areas: { id: string; name: string; code?: string }[] }[]) => {
    setFloors(generatedFloors.map(floor => ({
      id: floor.id, floor_name: floor.name, notes: '',
      areas: floor.areas.map(area => ({ ...createAreaDraft(area.name, area.code || ''), id: area.id })),
    })))
  }, [])

  const totalAreas = floors.reduce((acc, f) => acc + f.areas.length, 0)

  return {
    floors, setFloors, totalAreas,
    addFloor, removeFloor, updateFloor, updateFloorNotes,
    addArea, addBulkAreas, removeArea, updateArea,
    toggleAreaExpanded, addAreaAttachments, removeAreaAttachment,
    applyTemplate, applyGeneratedFloors,
  }
}
